import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useSiteChrome } from '../../context/SiteChromeContext';
import { pickSocialIcon, normaliseSocialHref } from '../../utils/socialIcons';

const quickLinks = [
  { label: 'Home', to: '/' },
  { label: 'About NIS Abuja', to: '/about' },
  { label: 'Find a Surveyor', to: '/find-surveyor' },
  { label: 'Contact Us', to: '/contact' },
];

const memberLinks = [
  { label: 'Member Login', to: '/login' },
  { label: 'Register', to: '/register' },
  { label: 'Member Portal', to: '/portal' },
  { label: 'MCPD Log', to: '/portal/mcpd' },
];

export default function Footer() {
  const { settings: siteSettings } = useSiteSettings();
  const { getFooter, getHeader } = useSiteChrome();
  const brandHeading = getFooter('Brand Heading')?.data?.text;
  const taglineHtml = getFooter('Brand Tagline')?.data?.html;
  const copyrightHtml = getFooter('Copyright')?.data?.html;
  const creditsHtml = getFooter('Credits')?.data?.html;
  const contactAddressHtml = getFooter('Contact Address')?.data?.html;
  const contactEmailHtml = getFooter('Contact Email')?.data?.html;
  const contactPhoneHtml = getFooter('Contact Phone')?.data?.html;
  const contactHoursHtml = getFooter('Contact Hours')?.data?.html;

  // The "Footer Columns" cards widget is the primary editable surface in the
  // page editor today (Quick Links / Members / Contact). Use it to drive the
  // three columns whenever it exists, so admin edits are reflected live.
  const footerCols = getFooter('Footer Columns')?.data?.items || [];
  const colByTitle = (needle) =>
    footerCols.find((c) => (c.title || '').toLowerCase().includes(needle.toLowerCase())) || null;
  const quickCol = colByTitle('quick') || footerCols[0] || null;
  const membersCol = colByTitle('member') || footerCols[1] || null;
  const contactCol = colByTitle('contact') || footerCols[2] || null;

  // Social Links: prefer the footer widget; fall back to the header widget so a
  // single set of handles can power both bars. Each item -> { title, description, icon }.
  // title       -> platform name (auto-mapped to icon)
  // description -> URL the icon links to
  const socialItemsRaw =
    getFooter('Social Links')?.data?.items ||
    getHeader('Social Links')?.data?.items ||
    [];
  const socialItems = socialItemsRaw
    .map((it) => ({ label: (it.title || '').trim(), href: normaliseSocialHref(it.description) }))
    .filter((it) => it.label && it.href);

  // Parse a description string like "Home | About | Contact" into label+href pairs.
  const slugify = (s) =>
    String(s).toLowerCase().trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const KNOWN_ROUTES = {
    home: '/', 'about nis abuja': '/about', about: '/about', 'find a surveyor': '/find-surveyor',
    'contact us': '/contact', contact: '/contact', 'member login': '/login', login: '/login',
    register: '/register', 'member portal': '/portal', portal: '/portal', 'mcpd log': '/portal/mcpd',
    mcpd: '/portal/mcpd', publications: '/publications', gallery: '/gallery',
    'mission & vision': '/mission-vision', constitution: '/constitution',
    'executive members': '/executive-members', subgroup: '/subgroup',
  };
  const parseLinks = (description, fallback) => {
    if (!description) return fallback;
    const parts = String(description).split(/\s*[|•·,]\s*|\s{2,}/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return fallback;
    return parts.map((label) => {
      const key = label.toLowerCase();
      const to = KNOWN_ROUTES[key] || `/${slugify(label)}`;
      return { label, to };
    });
  };
  const quickItems = parseLinks(quickCol?.description, quickLinks);
  const memberItems = parseLinks(membersCol?.description, memberLinks);

  // Contact column lines: prefer granular widgets; otherwise split the
  // "Contact" card description on big-spaces / line breaks.
  const contactLines = (() => {
    if (contactAddressHtml || contactEmailHtml || contactPhoneHtml || contactHoursHtml) {
      return null; // signal: render granular below
    }
    if (!contactCol?.description) return null;
    return String(contactCol.description)
      .split(/\u2003|\n|\r|\s{2,}/)
      .map((s) => s.trim())
      .filter(Boolean);
  })();

  const brandLines = (brandHeading || 'Nigerian Institution of Surveyors').split(/\s+(?=of\b)/i);
  return (
    <footer className="bg-[#062E10] text-white/70 mt-auto">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 pt-14 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={siteSettings?.header_logo || '/NIS ABJ LOGO.jpg'}
                alt="NIS FCT"
                className="h-12 w-12 rounded-full border-2 border-[#C8A951]/40 object-cover"
              />
              <div>
                <div className="text-white font-bold text-sm leading-tight">
                  {brandLines[0]}{brandLines[1] ? <><br/>{brandLines[1]}</> : null}
                </div>
              </div>
            </div>
            {taglineHtml ? (
              <div className="text-sm leading-relaxed mb-6 prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: taglineHtml }} />
            ) : (
              <p className="text-sm leading-relaxed mb-6">
                Promoting excellence in surveying practice, professional development,
                and the advancement of the surveying profession in Nigeria's FCT since 1934.
              </p>
            )}
            {/* Social Links — editable via the page editor (Site Footer → Social Links) */}
            <div className="flex items-center gap-3 flex-wrap">
              {(socialItems.length ? socialItems : [
                { label: 'Facebook',  href: 'https://facebook.com/' },
                { label: 'Twitter',   href: 'https://twitter.com/' },
                { label: 'LinkedIn',  href: 'https://linkedin.com/' },
                { label: 'Instagram', href: 'https://instagram.com/' },
              ]).map((social) => (
                <a
                  key={`${social.label}-${social.href}`}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/8 hover:bg-[#C8A951] hover:text-[#062E10] flex items-center justify-center transition-all duration-200"
                  aria-label={social.label}
                  title={social.label}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d={pickSocialIcon(social.label)}/></svg>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
              <span className="w-6 h-[2px] bg-[#C8A951]"></span>
              {quickCol?.title || 'Quick Links'}
            </h3>
            <ul className="space-y-3">
              {quickItems.map((link) => (
                <li key={`${link.label}-${link.to}`}>
                  <Link
                    to={link.to}
                    className="text-sm hover:text-[#C8A951] transition-colors flex items-center gap-2"
                  >
                    <svg className="w-3 h-3 text-[#C8A951]/60" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Members */}
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
              <span className="w-6 h-[2px] bg-[#C8A951]"></span>
              {membersCol?.title || 'Members'}
            </h3>
            <ul className="space-y-3">
              {memberItems.map((link) => (
                <li key={`${link.label}-${link.to}`}>
                  <Link
                    to={link.to}
                    className="text-sm hover:text-[#C8A951] transition-colors flex items-center gap-2"
                  >
                    <svg className="w-3 h-3 text-[#C8A951]/60" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5 flex items-center gap-2">
              <span className="w-6 h-[2px] bg-[#C8A951]"></span>
              {contactCol?.title || 'Contact'}
            </h3>
            {contactLines && contactLines.length > 0 ? (
              <ul className="space-y-4">
                {contactLines.map((line, idx) => (
                  <li key={`${idx}-${line}`} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#C8A951] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm break-words">{line}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#C8A951] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {contactAddressHtml ? (
                    <span className="text-sm prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: contactAddressHtml }} />
                  ) : (
                    <span className="text-sm">NIS FCT Branch Secretariat,<br/>Abuja, FCT, Nigeria</span>
                  )}
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#C8A951] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {contactEmailHtml ? (
                    <span className="text-sm prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: contactEmailHtml }} />
                  ) : (
                    <span className="text-sm">info@nisfct.org</span>
                  )}
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#C8A951] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {contactPhoneHtml ? (
                    <span className="text-sm prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: contactPhoneHtml }} />
                  ) : (
                    <span className="text-sm">+234-803-514-4023</span>
                  )}
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-[#C8A951] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {contactHoursHtml ? (
                    <span className="text-sm prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: contactHoursHtml }} />
                  ) : (
                    <span className="text-sm">Mon – Fri: 9:00 AM – 5:00 PM</span>
                  )}
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          {copyrightHtml ? (
            <div className="text-xs text-white/50 text-center sm:text-left" dangerouslySetInnerHTML={{ __html: copyrightHtml }} />
          ) : (
            <p className="text-xs text-white/50 text-center sm:text-left">
              &copy; {new Date().getFullYear()} Nigerian Institution of Surveyors, FCT Branch. All rights reserved.
            </p>
          )}
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span>Promoting Professional Surveying Since 1934</span>
          </div>
        </div>
        {creditsHtml ? (
          <div className="max-w-7xl mx-auto px-4 pb-4 text-center text-xs text-white/40" dangerouslySetInnerHTML={{ __html: creditsHtml }} />
        ) : (
          <div className="max-w-7xl mx-auto px-4 pb-4 text-center text-xs text-white/40">
            Developed by{' '}
            <a
              href="https://inmassat.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C8A951] hover:text-[#C8A951]/80 hover:underline font-medium"
            >
              Inmassat Global Network Company
            </a>
          </div>
        )}
      </div>
    </footer>
  );
}
